import { Component, HostListener, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { User } from '@auth0/auth0-spa-js';
import { Subscription } from 'rxjs';
import { Tenant } from 'src/app/core/model/tenants/tenant.model';
import { UserProfile } from 'src/app/core/model/tenants/user-profile.model';
import { AppService } from 'src/app/core/services/app.service';
import { TenantClientService } from 'src/app/core/services/tenant-client.service';
import { TenantHistoryService } from 'src/app/core/services/tenant-history.service';

@Component({
  selector: 'app-profile-menu',
  templateUrl: './profile-menu.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProfileMenuComponent implements OnInit, OnDestroy {
  isOpen = false;
  user: User | null | undefined;
  userProfile: UserProfile | null = null;
  currentTenant: Tenant | null = null;
  allTenants: Tenant[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    private appService: AppService,
    private auth: AuthService,
    private tenantClient: TenantClientService,
    private history: TenantHistoryService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.tenantClient.getTenants().subscribe((t) => (this.allTenants = t));
    this.subscriptions.push(
      this.auth.user$.subscribe((u) => (this.user = u)),
      this.appService.userProfileSubject.subscribe((p) => (this.userProfile = p)),
      this.appService.tenantSubject.subscribe((t) => (this.currentTenant = t)),
    );
  }

  get userName(): string {
    return this.userProfile?._links?.person?.name ?? this.userProfile?.email ?? '';
  }

  get personHref(): string | null {
    return this.userProfile?._links?.person?.href ?? null;
  }

  get isAdmin(): boolean {
    return this.appService.isAdmin();
  }

  get recentWorkspaces(): Tenant[] {
    return this.history
      .getHistory()
      .filter((code) => code !== this.currentTenant?.tenantCode)
      .slice(0, 3)
      .map((code) => this.allTenants.find((t) => t.tenantCode === code))
      .filter((t): t is Tenant => !!t);
  }

  toggle(event: Event): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.isOpen = false;
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  navigateTo(tenantCode: string): void {
    this.history.recordVisit(tenantCode);
    this.router.navigate(['/', tenantCode]);
    this.isOpen = false;
  }

  navigateToPerson(): void {
    if (this.personHref) {
      this.router.navigateByUrl(this.personHref);
      this.isOpen = false;
    }
  }

  navigateToSystem(): void {
    if (this.currentTenant) {
      this.router.navigate([this.currentTenant.tenantCode, 'system']);
      this.isOpen = false;
    }
  }

  logout(): void {
    this.auth.logout();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
